import { AnimationEvent } from '@angular/animations';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import moment from 'moment-timezone';

import { ActionKeys } from '@Common/Forms/enums/keys.enum';
import { DatePickerOverlayComponent } from '@Common/Forms/components/DatePickerOverlay/date-picker-overlay.component';
import { DatePickerPanelComponent } from '@Common/Forms/components/DatePickerPanel/date-picker-panel.component';
import { DatePickerService } from '@Common/Forms/services/date-picker.service';


/**
 * Dropdown-style single date picker with an optional "most recent" date.
 *
 * - The trigger looks like a select; clicking it (or ArrowDown) opens the calendar.
 * - When the selected date is `mostRecentDate`, the label is suffixed with "(Most Recent)".
 * - A "Most Recent" button under the calendar jumps straight to `mostRecentDate`.
 *
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - id of an external label; it is combined with the selected value
 * + ***id*** - unique id for the trigger button
 */
@Component({
    selector: 'agpr-vi-date-picker',
    templateUrl: './vi-date-picker.component.html',
    styleUrls: ['./vi-date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ViDatePickerComponent),
            multi: true
        },
        DatePickerService
    ],
    standalone: false
})
export class ViDatePickerComponent implements ControlValueAccessor {

    @Input() id: string = 'viDatePicker';
    @Input() ariaLabelledBy: string;
    @Input() placeholder: string;
    @Input() disabled: boolean;
    @Input() panelStyleClass: string;
    @Input() icon: string = 'fa-duotone fa-regular fa-calendar-range';
    /** moment format used for the trigger label, e.g. "May 26 2026". */
    @Input() displayFormat: string = 'MMM D YYYY';

    /** The date flagged as "(Most Recent)". Also where the calendar opens when nothing is selected. */
    @Input() mostRecentDate: Date;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() disabledDates: Date[];
    @Input() disabledDays: number[];

    /** Days that have data. Others are shown greyed out and can't be picked. */
    @Input() availableDates: (Date | string)[] | null;

    @Output() select = new EventEmitter<Date>();
    @Output() show = new EventEmitter<AnimationEvent>();
    @Output() close = new EventEmitter<AnimationEvent>();
    @Output() monthChange = new EventEmitter<{ month: number; year: number }>();
    @Output() yearChange = new EventEmitter<{ month: number; year: number }>();

    @ViewChild('trigger', { static: true }) triggerRef: ElementRef<HTMLButtonElement>;
    @ViewChild('panel', { static: true }) panel: DatePickerPanelComponent;
    @ViewChild('overlay', { static: true }) overlay: DatePickerOverlayComponent;

    value: Date = null;

    constructor(
        private cd: ChangeDetectorRef,
        private datePickerService: DatePickerService
    ) {}

    get isOpen(): boolean {
        return !!this.overlay?.visible;
    }

    get displayValue(): string {
        return this.value ? moment(this.value).format(this.displayFormat) : '';
    }

    get isMostRecent(): boolean {
        return !!this.value && !!this.mostRecentDate && moment(this.value).isSame(this.mostRecentDate, 'day');
    }


    writeValue(value: Date | string | null): void {
        const parsed = value ? moment(value) : null;
        this.value = parsed?.isValid() ? parsed.toDate() : null;
        this.cd.markForCheck();
    }

    private onModelChange: (value: Date) => void = () => {};
    private onModelTouched: () => void = () => {};

    registerOnChange(fn: (value: Date) => void): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.closePanel();
        }
        this.cd.markForCheck();
    }


    togglePanel(): void {
        if (this.isOpen) {
            this.closePanel();
        } else {
            this.openPanel();
        }
    }

    openPanel(): void {
        if (this.isOpen || this.disabled) {
            return;
        }
        // Open on the selected month; with nothing selected the panel falls back to mostRecentDate.
        this.panel.navigateTo(this.value);
        this.cd.detectChanges();
        this.overlay.open();
    }

    closePanel(returnFocus = false): void {
        this.overlay?.close();
        if (returnFocus) {
            this.triggerRef.nativeElement.focus();
        }
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        if (event.key === ActionKeys.DOWN) {
            this.openPanel();
            event.preventDefault();
        } else if (event.key === ActionKeys.ESCAPE && this.isOpen) {
            this.closePanel();
        }
    }

    onOverlayOpened(event: AnimationEvent): void {
        this.panel.focusInitial();
        this.show.emit(event);
    }

    onOverlayClosed(event: AnimationEvent): void {
        this.panel.resetView();
        this.onModelTouched();
        this.close.emit(event);
    }


    onPanelValueChange(value: Date): void {
        this.updateModel(value);
    }

    onPanelDateSelect(date: Date): void {
        this.select.emit(date);
        requestAnimationFrame(() => this.closePanel(true));
    }

    selectMostRecent(): void {
        if (!this.mostRecentDate) {
            return;
        }
        const date = new Date(this.mostRecentDate);
        this.updateModel(date);
        this.select.emit(date);
        this.closePanel(true);
    }

    private updateModel(value: Date): void {
        this.value = value;
        this.onModelChange(value);
    }
}
